/* eslint-disable max-len, react/jsx-no-bind, react/jsx-no-literals, react/jsx-max-props-per-line, react/jsx-wrap-multilines */
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'scratch-vm';

import {costumeUpload, soundUpload} from '../../lib/file-uploader.js';
import Box from '../box/box.jsx';
import Modal from '../modal/modal.jsx';

import styles from './ai-assist.css';

const requestApi = async (path, options = {}) => {
    const response = await fetch(`/api/${path}`, {
        credentials: 'same-origin',
        headers: {'content-type': 'application/json', ...(options.headers || {})},
        ...options
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Không thể hoàn tất yêu cầu AI.');
    return result;
};

const postApi = (path, body, method = 'POST') => requestApi(path, {
    method,
    body: JSON.stringify(body)
});

const base64ToBuffer = value => {
    const binary = window.atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
    return bytes.buffer;
};

const cleanName = value => String(value || 'tai-nguyen-ai')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60) || 'tai-nguyen-ai';

const list = value => (Array.isArray(value) ? value : []);
const directionsText = scene => list(scene.characterDirections).map(direction => [
    direction.character, direction.pose, direction.gesture, direction.facing, direction.expression, direction.blocking
].filter(Boolean)
    .join(' · '))
    .join('\n');
const animationText = scene => list(scene.animationPlan).map(step => [
    `${step.startSeconds || 0}s`, step.character, step.action, step.costume, step.easing
].filter(Boolean)
    .join(' · '))
    .join('\n');
const characterNames = episode => list(episode.characters)
    .map(character => character.name)
    .filter(Boolean)
    .join(', ');
const sceneTimeline = episode => episode.scenes
    .map((scene, index) => `Cảnh ${index + 1}: ${scene.title} (${scene.startSeconds || 0}s–${(scene.startSeconds || 0) + (scene.durationSeconds || 0)}s)`)
    .join(' | ');
const animationSummary = episode => episode.scenes
    .map((scene, index) => `Cảnh ${index + 1}: ${list(scene.characterDirections).length} chỉ dẫn nhân vật, ${list(scene.animationPlan).length} nhịp animation`)
    .join(' | ');
const assetSummary = episode => episode.scenes
    .flatMap(scene => list(scene.assetPrompts))
    .map(asset => `${asset.kind}: ${asset.name}`)
    .join(' | ');
const dialogueSummary = episode => episode.scenes
    .flatMap(scene => list(scene.dialogue))
    .map(line => `${line.speaker}: ${line.text}`)
    .join(' | ');
const formatDateTime = value => value ? new Intl.DateTimeFormat('vi-VN', {
    dateStyle: 'short', timeStyle: 'short'
}).format(new Date(value)) : 'Chưa có';
const fileBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(new Error('Không đọc được file ảnh.'));
    reader.readAsDataURL(file);
});

const normalizeEpisode = episode => ({
    ...episode,
    title: episode.title || 'Tập phim chưa có tên',
    logline: episode.logline || '',
    scenes: list(episode.scenes).map((scene, index) => ({
        ...scene,
        id: scene.id || `scene-${index + 1}`,
        title: scene.title || `Cảnh ${index + 1}`,
        summary: scene.summary || '',
        background: scene.background || '',
        backgroundPrompt: scene.backgroundPrompt || scene.background || '',
        action: scene.action || '',
        characterDirections: list(scene.characterDirections),
        animationPlan: list(scene.animationPlan),
        dialogue: list(scene.dialogue),
        soundEffects: list(scene.soundEffects),
        assetPrompts: list(scene.assetPrompts)
    }))
});

class FilmMakerAIAssist extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            activeTab: 'story',
            brief: '',
            durationSeconds: 30,
            episode: null,
            episodeId: null,
            series: [],
            selectedSeriesId: '',
            selectedSeries: null,
            creatingSeries: false,
            seriesDraft: {title: '', premise: '', visualStyle: ''},
            characterDraft: {name: '', role: '', description: '', designPrompt: '', voice: ''},
            editingCharacterId: null,
            assetView: 'grid',
            episodeView: 'grid',
            assetDraft: {name: '', kind: 'prop', prompt: '', characterId: ''},
            editingAssetId: null,
            assetType: 'background',
            assetCharacterId: '',
            imagePrompt: '',
            imageName: '',
            image: null,
            voiceText: '',
            selectedVoice: '',
            voices: null,
            generationSteps: [],
            isWorking: false,
            status: '',
            error: ''
        };
        this.progressTimer = null;
        this.storyResult = null;
        this.handleGenerateEpisode = this.handleGenerateEpisode.bind(this);
        this.handleGenerateImage = this.handleGenerateImage.bind(this);
        this.handleAddImage = this.handleAddImage.bind(this);
        this.handleGenerateSpeech = this.handleGenerateSpeech.bind(this);
        this.handleCreateSeries = this.handleCreateSeries.bind(this);
        this.handleSaveSeries = this.handleSaveSeries.bind(this);
        this.handleSaveCharacter = this.handleSaveCharacter.bind(this);
        this.handleSaveEpisode = this.handleSaveEpisode.bind(this);
        this.handleCreateSeriesAndSaveEpisode = this.handleCreateSeriesAndSaveEpisode.bind(this);
        this.handleSaveImageToSeries = this.handleSaveImageToSeries.bind(this);
        this.handleUploadSeriesAsset = this.handleUploadSeriesAsset.bind(this);
        this.handleSaveSeriesAsset = this.handleSaveSeriesAsset.bind(this);
    }
    componentDidMount () {
        Promise.all([
            requestApi('config'),
            requestApi('series')
        ]).then(([config, data]) => this.setState({
            voices: config.voices,
            selectedVoice: config.voices.characterA,
            series: data.series
        }))
            .catch(error => this.setState({error: error.message}));
    }
    componentWillUnmount () {
        this.stopProgress();
        this.storyResult = null;
    }
    setTab (activeTab) {
        this.setState({activeTab, error: '', status: ''});
    }
    setWorking (isWorking, next = {}) {
        this.setState({...next, isWorking});
    }
    startProgress () {
        const steps = [
            {label: 'Phân tích ý tưởng, logline và cast', state: 'active'},
            {label: 'Lập timeline và mục tiêu từng scene', state: 'pending'},
            {label: 'Viết blocking, cử chỉ và animation plan', state: 'pending'},
            {label: 'Lập prompt background, costume và prop', state: 'pending'},
            {label: 'Hoàn thiện thoại và sound cues', state: 'pending'}
        ];
        this.setState({generationSteps: steps});
        let active = 0;
        this.progressTimer = setInterval(() => {
            active = Math.min(active + 1, steps.length - 1);
            this.setState({generationSteps: steps.map((step, index) => ({
                ...step,
                state: index < active ? 'done' : index === active ? 'active' : 'pending'
            }))});
        }, 1600);
    }
    stopProgress (complete = false) {
        if (this.progressTimer) {
            clearInterval(this.progressTimer);
        }
        this.progressTimer = null;
        if (complete) {
            this.setState(state => ({
                generationSteps: state.generationSteps.map(step => ({...step, state: 'done'}))
            }));
        }
    }
    focusStoryResult () {
        if (this.storyResult) {
            this.storyResult.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }
    async refreshSeries (selectedId = this.state.selectedSeriesId) {
        const data = await requestApi('series');
        this.setState({series: data.series});
        if (selectedId) await this.selectSeries(selectedId);
    }
    async selectSeries (seriesId) {
        if (!seriesId) {
            this.setState({selectedSeriesId: '', selectedSeries: null, episodeId: null});
            return;
        }
        try {
            const data = await requestApi(`series/${encodeURIComponent(seriesId)}`);
            const selectedSeries = data.series;
            this.setState({
                selectedSeriesId: selectedSeries.id,
                selectedSeries,
                seriesDraft: {
                    title: selectedSeries.title,
                    premise: selectedSeries.premise,
                    visualStyle: selectedSeries.visualStyle
                },
                status: `Đã nạp series “${selectedSeries.title}” cùng character bible và asset bộ.`
            });
        } catch (error) {
            this.setState({error: error.message, selectedSeriesId: '', selectedSeries: null});
        }
    }
    async handleCreateSeries () {
        const title = this.state.seriesDraft.title.trim();
        if (!title) {
            this.setState({error: 'Hãy đặt tên cho series.'});
            return;
        }
        this.setWorking(true, {error: '', status: 'Đang tạo series…'});
        try {
            const data = await postApi('series', this.state.seriesDraft);
            await this.refreshSeries(data.series.id);
            this.setWorking(false, {creatingSeries: false, status: 'Series đã sẵn sàng. Hãy thêm character bible hoặc bắt đầu tập đầu tiên.'});
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    async handleSaveSeries () {
        if (!this.state.selectedSeriesId) return;
        this.setWorking(true, {error: '', status: 'Đang lưu series bible…'});
        try {
            const data = await postApi(`series/${this.state.selectedSeriesId}`, this.state.seriesDraft, 'PUT');
            await this.refreshSeries(data.series.id);
            this.setWorking(false, {status: 'Đã lưu series bible.', error: ''});
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    async handleSaveCharacter () {
        const seriesId = this.state.selectedSeriesId;
        const character = this.state.characterDraft;
        if (!seriesId) {
            this.setState({error: 'Hãy tạo hoặc chọn một series trước.'});
            return;
        }
        if (!character.name.trim()) {
            this.setState({error: 'Nhân vật cần có tên.'});
            return;
        }
        this.setWorking(true, {error: '', status: 'Đang lưu character bible…'});
        try {
            const path = this.state.editingCharacterId ?
                `series/${seriesId}/characters/${this.state.editingCharacterId}` : `series/${seriesId}/characters`;
            await postApi(path, character, this.state.editingCharacterId ? 'PUT' : 'POST');
            await this.refreshSeries(seriesId);
            this.setWorking(false, {
                characterDraft: {name: '', role: '', description: '', designPrompt: '', voice: ''},
                editingCharacterId: null,
                status: 'Đã lưu nhân vật. Kịch bản AI mới sẽ dùng đúng mô tả này.',
                error: ''
            });
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    async handleGenerateEpisode () {
        const brief = this.state.brief.trim();
        if (!brief) {
            this.setState({error: 'Hãy mô tả câu chuyện bạn muốn tạo.'});
            return;
        }
        this.setWorking(true, {error: '', status: 'Gemini đang dựng screenplay theo từng scene…'});
        this.startProgress();
        try {
            const result = await postApi('episode', {
                brief,
                durationSeconds: Number(this.state.durationSeconds),
                seriesId: this.state.selectedSeriesId || null
            });
            const episode = normalizeEpisode(result.episode);
            this.stopProgress(true);
            this.setState({
                isWorking: false,
                episode,
                episodeId: null,
                status: this.state.selectedSeriesId ?
                    `Gemini đã trả về ${episode.scenes.length} scene. Đã mở screenplay editor tại Scene 1.` :
                    `Gemini đã trả về ${episode.scenes.length} scene. Tạo series để lưu và tái sử dụng asset bible.`,
                error: ''
            }, () => this.focusStoryResult());
        } catch (error) {
            this.stopProgress();
            this.setWorking(false, {generationSteps: [], error: error.message, status: ''});
        }
    }
    updateEpisode (changes) {
        this.setState(state => ({episode: {...state.episode, ...changes}}));
    }
    updateScene (sceneIndex, changes) {
        this.setState(state => ({episode: {
            ...state.episode,
            scenes: state.episode.scenes.map((scene, index) => (
                index === sceneIndex ? {...scene, ...changes} : scene
            ))
        }}));
    }
    updateDialogue (sceneIndex, dialogueIndex, changes) {
        this.setState(state => ({episode: {
            ...state.episode,
            scenes: state.episode.scenes.map((scene, index) => (
                index === sceneIndex ? {
                    ...scene,
                    dialogue: scene.dialogue.map((line, lineIndex) => (
                        lineIndex === dialogueIndex ? {...line, ...changes} : line
                    ))
                } : scene
            ))
        }}));
    }
    async handleSaveEpisode () {
        const {episode, selectedSeriesId, episodeId} = this.state;
        if (!episode) return;
        if (!selectedSeriesId) {
            this.setState({activeTab: 'series', error: 'Tạo hoặc chọn một series trước khi lưu tập phim.'});
            return;
        }
        this.setWorking(true, {error: '', status: 'Đang lưu tập phim và từng scene…'});
        try {
            const payload = {
                title: episode.title,
                logline: episode.logline,
                brief: this.state.brief,
                durationSeconds: Number(this.state.durationSeconds),
                scratchProjectId: this.props.projectId,
                scenes: episode.scenes
            };
            const data = episodeId ?
                await postApi(`episodes/${episodeId}`, payload, 'PUT') :
                await postApi(`series/${selectedSeriesId}/episodes`, payload);
            await this.refreshSeries(selectedSeriesId);
            this.setWorking(false, {
                episode: normalizeEpisode(data.episode),
                episodeId: data.episode.id,
                status: 'Đã lưu episode, scenes và liên kết với Scratch project hiện tại.',
                error: ''
            });
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    async handleCreateSeriesAndSaveEpisode () {
        const {episode} = this.state;
        if (!episode) return;
        this.setWorking(true, {error: '', status: 'Đang tạo series và lưu tập phim…'});
        try {
            const created = await postApi('series', {
                title: `${episode.title} · Series`,
                premise: this.state.brief || episode.logline,
                visualStyle: ''
            });
            const seriesId = created.series.id;
            const payload = {
                title: episode.title,
                logline: episode.logline,
                brief: this.state.brief,
                durationSeconds: Number(this.state.durationSeconds),
                scratchProjectId: this.props.projectId,
                scenes: episode.scenes
            };
            const saved = await postApi(`series/${seriesId}/episodes`, payload);
            const [seriesList, selected] = await Promise.all([
                requestApi('series'),
                requestApi(`series/${seriesId}`)
            ]);
            this.setWorking(false, {
                series: seriesList.series,
                selectedSeriesId: seriesId,
                selectedSeries: selected.series,
                seriesDraft: {
                    title: selected.series.title,
                    premise: selected.series.premise,
                    visualStyle: selected.series.visualStyle
                },
                episode: normalizeEpisode(saved.episode),
                episodeId: saved.episode.id,
                status: 'Đã tạo series, lưu episode và liên kết với Scratch project hiện tại.',
                error: ''
            });
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    async loadEpisode (episodeId) {
        this.setWorking(true, {error: '', status: 'Đang nạp screenplay…'});
        try {
            const data = await requestApi(`episodes/${episodeId}`);
            const episode = normalizeEpisode(data.episode);
            this.setWorking(false, {
                activeTab: 'story',
                episode,
                episodeId: episode.id,
                brief: episode.brief,
                durationSeconds: episode.durationSeconds || this.state.durationSeconds,
                status: `Đã nạp Episode ${episode.number}: ${episode.title}.`,
                error: ''
            });
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    chooseDialogue (dialogue) {
        const voices = this.state.voices || {};
        const speaker = String(dialogue.speaker || '').toLowerCase();
        const selectedVoice = speaker.includes('nam') ? voices.characterB : voices.characterA;
        this.setState({
            activeTab: 'voice',
            voiceText: dialogue.text || '',
            selectedVoice: selectedVoice || this.state.selectedVoice,
            status: 'Câu thoại đã được đưa sang phần Giọng nói.',
            error: ''
        });
    }
    async handleGenerateSpeech () {
        const text = this.state.voiceText.trim();
        if (!text) {
            this.setState({error: 'Hãy nhập lời thoại trước khi tạo giọng nói.'});
            return;
        }
        if (!this.state.selectedVoice || !this.props.vm.editingTarget) {
            this.setState({error: 'Hãy chọn giọng nói và một nhân vật hoặc Sân khấu trong Scratch trước.'});
            return;
        }
        this.setWorking(true, {error: '', status: 'Gemini đang tạo giọng nói…'});
        try {
            const result = await postApi('speech', {text, voice: this.state.selectedVoice});
            const storage = this.props.vm.runtime.storage;
            await new Promise((resolve, reject) => {
                soundUpload(base64ToBuffer(result.wavBase64), result.wavMimeType || 'audio/wav', storage, sound => {
                    sound.name = cleanName(`AI ${text.slice(0, 35)}`);
                    this.props.vm.addSound(sound, this.props.vm.editingTarget.id).then(resolve, reject);
                }, reject);
            });
            this.setWorking(false, {status: 'Đã thêm giọng nói vào Sounds của đối tượng đang chọn.', error: ''});
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    async handleGenerateImage () {
        const basePrompt = this.state.imagePrompt.trim();
        if (!basePrompt) {
            this.setState({error: 'Hãy mô tả hình ảnh bạn muốn tạo.'});
            return;
        }
        const character = this.state.selectedSeries && this.state.selectedSeries.characters.find(item =>
            item.id === this.state.assetCharacterId
        );
        const prompt = character && character.designPrompt ?
            `${basePrompt}\nGiữ nhất quán character bible: ${character.designPrompt}` : basePrompt;
        this.setWorking(true, {error: '', status: 'Gemini đang vẽ tài nguyên 2D…', image: null});
        try {
            const image = await postApi('image', {prompt, assetType: this.state.assetType, name: this.state.imageName});
            this.setWorking(false, {image, status: 'Ảnh đã sẵn sàng. Bạn có thể thêm vào Scratch hoặc lưu cho cả series.', error: ''});
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    async addImageBufferToScratch (buffer, image) {
        const target = this.props.vm.editingTarget;
        if (image.assetType !== 'background' && (!target || target.isStage)) {
            throw new Error('Để thêm trang phục hoặc đạo cụ, hãy chọn một nhân vật trong Scratch trước.');
        }
        const storage = this.props.vm.runtime.storage;
        await new Promise((resolve, reject) => {
            costumeUpload(buffer, image.mimeType, storage, costumes => {
                const tasks = costumes.map((costume, index) => {
                    const asset = {
                        ...costume,
                        name: `${cleanName(image.suggestedName || image.name)}${index ? `-${index + 1}` : ''}`,
                        skinId: null
                    };
                    return image.assetType === 'background' ?
                        this.props.vm.addBackdrop(asset.md5, asset) : this.props.vm.addCostume(asset.md5, asset, target.id);
                });
                Promise.all(tasks).then(resolve, reject);
            }, reject);
        });
    }
    async handleAddImage () {
        if (!this.state.image) return;
        this.setWorking(true, {error: '', status: 'Đang thêm tài nguyên vào Scratch…'});
        try {
            await this.addImageBufferToScratch(base64ToBuffer(this.state.image.imageBase64), this.state.image);
            this.setWorking(false, {status: 'Đã thêm tài nguyên vào Scratch.', error: ''});
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    async handleSaveImageToSeries () {
        const {image, selectedSeriesId, assetCharacterId} = this.state;
        if (!image || !selectedSeriesId) return;
        this.setWorking(true, {error: '', status: 'Đang lưu asset vào thư viện chung của series…'});
        try {
            await postApi(`series/${selectedSeriesId}/assets`, {
                imageBase64: image.imageBase64,
                mimeType: image.mimeType,
                kind: image.assetType,
                name: image.suggestedName,
                prompt: this.state.imagePrompt,
                characterId: assetCharacterId || null
            });
            await this.refreshSeries(selectedSeriesId);
            this.setWorking(false, {status: 'Đã lưu asset vào series. Tập khác có thể nạp lại đúng asset này.', error: ''});
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    beginAssetEdit (asset = null) {
        this.setState({
            editingAssetId: asset ? asset.id : null,
            assetDraft: asset ? {
                name: asset.name || '',
                kind: asset.kind || 'prop',
                prompt: asset.prompt || '',
                characterId: asset.characterId || ''
            } : {name: '', kind: 'prop', prompt: '', characterId: ''},
            error: '',
            status: asset ? `Đang sửa metadata của ${asset.name}.` : 'Chọn một file ảnh để thêm asset mới.'
        });
    }
    async handleUploadSeriesAsset (event) {
        const file = event.target.files && event.target.files[0];
        event.target.value = '';
        if (!file) return;
        if (!this.state.selectedSeriesId) {
            this.setState({error: 'Hãy tạo hoặc chọn một series trước khi tải ảnh lên.'});
            return;
        }
        if (!['image/png', 'image/jpeg'].includes(file.type)) {
            this.setState({error: 'Thư viện series hiện nhận ảnh PNG hoặc JPEG.'});
            return;
        }
        if (file.size > 7000000) {
            this.setState({error: 'Ảnh lớn hơn giới hạn 7 MB khi tải qua API. Hãy tối ưu ảnh rồi thử lại.'});
            return;
        }
        this.setWorking(true, {error: '', status: `Đang tải ${file.name} vào thư viện series…`});
        try {
            const base64 = await fileBase64(file);
            const draft = this.state.assetDraft;
            await postApi(`series/${this.state.selectedSeriesId}/assets`, {
                imageBase64: base64,
                mimeType: file.type,
                kind: draft.kind,
                name: draft.name.trim() || file.name.replace(/\.[^.]+$/, ''),
                prompt: draft.prompt,
                characterId: draft.characterId || null
            });
            await this.refreshSeries(this.state.selectedSeriesId);
            this.setWorking(false, {
                assetDraft: {name: '', kind: 'prop', prompt: '', characterId: ''},
                editingAssetId: null,
                status: 'Đã thêm ảnh vào thư viện asset dùng chung.',
                error: ''
            });
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    async handleSaveSeriesAsset () {
        const {selectedSeriesId, editingAssetId, assetDraft} = this.state;
        if (!selectedSeriesId || !editingAssetId) return;
        if (!assetDraft.name.trim()) {
            this.setState({error: 'Asset cần có tên.'});
            return;
        }
        this.setWorking(true, {error: '', status: 'Đang cập nhật asset…'});
        try {
            await postApi(`series/${selectedSeriesId}/assets/${editingAssetId}`, assetDraft, 'PUT');
            await this.refreshSeries(selectedSeriesId);
            this.setWorking(false, {
                assetDraft: {name: '', kind: 'prop', prompt: '', characterId: ''},
                editingAssetId: null,
                status: 'Đã cập nhật metadata asset.',
                error: ''
            });
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    async loadSeriesAsset (asset) {
        this.setWorking(true, {error: '', status: `Đang nạp ${asset.name} vào Scratch…`});
        try {
            const response = await fetch(asset.url, {credentials: 'same-origin'});
            if (!response.ok) throw new Error('Không tải được asset của series.');
            await this.addImageBufferToScratch(await response.arrayBuffer(), {
                assetType: asset.kind,
                mimeType: asset.mimeType,
                suggestedName: asset.name
            });
            this.setWorking(false, {status: `Đã nạp ${asset.name} vào Scratch.`, error: ''});
        } catch (error) {
            this.setWorking(false, {error: error.message, status: ''});
        }
    }
    useAssetPrompt (asset) {
        this.setState({
            activeTab: 'image',
            assetType: asset.kind === 'background' ? 'background' : asset.kind === 'prop' ? 'prop' : 'costume',
            imageName: asset.name || '',
            imagePrompt: asset.prompt || '',
            image: null,
            status: 'Prompt asset đã sẵn sàng để tạo bằng Gemini.',
            error: ''
        });
    }
    renderProgress () {
        if (!this.state.generationSteps.length) return null;
        const progressStyle = state => (
            state === 'done' ? styles.progressDone :
                state === 'active' ? styles.progressActive : styles.progressPending
        );
        const episode = this.state.episode;
        const details = episode ? [
            `${episode.logline} · Cast: ${characterNames(episode)}`,
            sceneTimeline(episode),
            animationSummary(episode),
            assetSummary(episode),
            dialogueSummary(episode)
        ] : [];
        return <div className={styles.progress} aria-live="polite">
            {this.state.generationSteps.map((step, index) => <div className={styles.progressStep} key={step.label}>
                <span className={progressStyle(step.state)}>{step.state === 'done' ? '✓' : index + 1}</span>
                <span><strong>{step.label}</strong>{details[index] ? <span className={styles.progressDetail}>{details[index]}</span> : null}</span>
            </div>)}
        </div>;
    }
    renderScene (scene, sceneIndex) {
        return <section className={styles.scene} key={scene.id || sceneIndex}>
            <div className={styles.sceneHeading}>Cảnh {sceneIndex + 1} · {scene.startSeconds || 0}s — {scene.durationSeconds || 0}s</div>
            <div className={styles.sceneBody}>
                <label className={styles.label}>Tên cảnh</label>
                <input className={styles.input} value={scene.title} onChange={event => this.updateScene(sceneIndex, {title: event.target.value})} />
                <label className={styles.label}>Tóm tắt diễn biến</label>
                <textarea className={styles.compactTextarea} value={scene.summary} onChange={event => this.updateScene(sceneIndex, {summary: event.target.value})} />
                <label className={styles.label}>Prompt tạo ảnh nền</label>
                <textarea className={styles.compactTextarea} value={scene.backgroundPrompt} onChange={event => this.updateScene(sceneIndex, {backgroundPrompt: event.target.value})} />
                <div className={styles.inlineActions}>
                    <button className={styles.linkButton} onClick={() => this.useAssetPrompt({kind: 'background', name: `${scene.title}-nen`, prompt: scene.backgroundPrompt})}>Tạo nền bằng AI</button>
                </div>
                <label className={styles.label}>Blocking, cử chỉ và biểu cảm nhân vật</label>
                <textarea className={styles.compactTextarea} value={scene.characterDirectionNotes || directionsText(scene)} onChange={event => this.updateScene(sceneIndex, {characterDirectionNotes: event.target.value})} />
                <label className={styles.label}>Animation chi tiết</label>
                <textarea className={styles.compactTextarea} value={scene.animationNotes || animationText(scene) || scene.action} onChange={event => this.updateScene(sceneIndex, {animationNotes: event.target.value})} />
                <label className={styles.label}>Lời thoại</label>
                {list(scene.dialogue).map((dialogue, dialogueIndex) => <div className={styles.dialogueEditor} key={`${sceneIndex}-${dialogueIndex}`}>
                    <input className={styles.speakerInput} value={dialogue.speaker || ''} onChange={event => this.updateDialogue(sceneIndex, dialogueIndex, {speaker: event.target.value})} />
                    <input className={styles.dialogueInput} value={dialogue.text || ''} onChange={event => this.updateDialogue(sceneIndex, dialogueIndex, {text: event.target.value})} />
                    <button className={styles.linkButton} onClick={() => this.chooseDialogue(dialogue)}>Tạo giọng</button>
                </div>)}
                {list(scene.assetPrompts).length ? <div className={styles.assetSuggestions}>
                    <strong>Asset đề xuất</strong>
                    {list(scene.assetPrompts).map((asset, assetIndex) => <div className={styles.assetSuggestion} key={`${sceneIndex}-asset-${assetIndex}`}>
                        <span><strong>{asset.name || asset.kind}</strong><small>{asset.kind} · {asset.prompt || 'Chưa có prompt'}</small></span>
                        <button className={styles.linkButton} onClick={() => this.useAssetPrompt(asset)}>Dùng prompt</button>
                    </div>)}
                </div> : null}
            </div>
        </section>;
    }
    renderProductionOutput (episode) {
        return <section className={styles.productionOutput} aria-live="polite">
            <h3>Screenplay AI đã tạo · {episode.scenes.length} scene</h3>
            <div className={styles.outputSection}>
                <h4>1. Ý tưởng, logline và cast</h4>
                <p><strong>Logline:</strong> {episode.logline}</p>
                <p><strong>Nhân vật:</strong> {characterNames(episode)}</p>
            </div>
            <div className={styles.outputSection}>
                <h4>2. Timeline và mục tiêu từng scene</h4>
                {episode.scenes.map((scene, index) => <p key={`timeline-${scene.id || index}`}><strong>Cảnh {index + 1} · {scene.startSeconds || 0}s–{(scene.startSeconds || 0) + (scene.durationSeconds || 0)}s · {scene.title}</strong><br />{scene.summary}</p>)}
            </div>
            <div className={styles.outputSection}>
                <h4>3. Blocking, cử chỉ và animation</h4>
                {episode.scenes.map((scene, index) => <div className={styles.outputScene} key={`animation-${scene.id || index}`}>
                    <strong>Cảnh {index + 1}: {scene.title}</strong>
                    <p><em>Blocking:</em><br />{directionsText(scene) || 'Chưa có chỉ dẫn nhân vật'}</p>
                    <p><em>Animation:</em><br />{animationText(scene) || scene.action || 'Chưa có nhịp animation'}</p>
                </div>)}
            </div>
            <div className={styles.outputSection}>
                <h4>4. Prompt nền, costume và props</h4>
                {episode.scenes.map((scene, index) => <div className={styles.outputScene} key={`assets-${scene.id || index}`}>
                    <strong>Cảnh {index + 1}: {scene.title}</strong>
                    <p><em>Background:</em><br />{scene.backgroundPrompt || 'Chưa có prompt nền'}</p>
                    {list(scene.assetPrompts).map((asset, assetIndex) => <p className={styles.outputAsset} key={`asset-output-${scene.id || index}-${assetIndex}`}><strong>{asset.kind}: {asset.name}</strong><br />{asset.prompt || 'Chưa có prompt'}</p>)}
                </div>)}
            </div>
            <div className={styles.outputSection}>
                <h4>5. Lời thoại và sound cues</h4>
                {episode.scenes.map((scene, index) => <div className={styles.outputScene} key={`dialogue-${scene.id || index}`}>
                    <strong>Cảnh {index + 1}: {scene.title}</strong>
                    {list(scene.dialogue).map((line, dialogueIndex) => <p key={`line-output-${scene.id || index}-${dialogueIndex}`}><strong>{line.speaker}:</strong> {line.text} {line.emotion ? `(${line.emotion})` : ''}</p>)}
                    {list(scene.soundEffects).length ? <p><em>Sound cues:</em> {scene.soundEffects.join(' · ')}</p> : null}
                </div>)}
            </div>
        </section>;
    }
    renderStory () {
        const episode = this.state.episode;
        const saveEpisode = this.state.selectedSeriesId ? this.handleSaveEpisode : this.handleCreateSeriesAndSaveEpisode;
        const saveLabel = this.state.selectedSeriesId ?
            (this.state.episodeId ? 'Lưu cập nhật episode' : 'Lưu episode và scenes') :
            'Tạo series & lưu episode';
        return (<React.Fragment>
            <p className={styles.intro}>Tạo screenplay theo scene, rồi chỉnh sửa prompt ảnh, blocking, animation và thoại trước khi dựng trong Scratch.</p>
            <div className={styles.seriesBanner}>
                <span>Series: <strong>{this.state.selectedSeries ? this.state.selectedSeries.title : 'Chưa chọn'}</strong></span>
                <button className={styles.linkButton} onClick={() => this.setTab('series')}>Quản lý series</button>
            </div>
            <label className={styles.label} htmlFor="film-maker-ai-brief">Ý tưởng tập phim</label>
            <textarea id="film-maker-ai-brief" className={styles.textarea} maxLength="6000" placeholder="Ví dụ: Nam và An giúp Miu tìm chiếc chuông bị rơi trong công viên lúc hoàng hôn." value={this.state.brief} onChange={event => this.setState({brief: event.target.value})} />
            <div className={styles.actionRow}>
                <label className={styles.inlineLabel} htmlFor="film-maker-ai-duration">Thời lượng</label>
                <select id="film-maker-ai-duration" className={styles.select} value={this.state.durationSeconds} onChange={event => this.setState({durationSeconds: event.target.value})}>
                    <option value="15">15 giây</option><option value="30">30 giây</option><option value="45">45 giây</option><option value="60">60 giây</option>
                </select>
                <button className={styles.primaryButton} disabled={this.state.isWorking} onClick={this.handleGenerateEpisode}>Tạo screenplay</button>
            </div>
            {this.renderProgress()}
            {episode ? <div
                className={styles.storyResult}
                ref={element => {
                    this.storyResult = element;
                }}
            >
                {this.renderProductionOutput(episode)}
                <label className={styles.label}>Tên tập</label>
                <input className={styles.titleInput} value={episode.title} onChange={event => this.updateEpisode({title: event.target.value})} />
                <label className={styles.label}>Logline</label>
                <textarea className={styles.compactTextarea} value={episode.logline} onChange={event => this.updateEpisode({logline: event.target.value})} />
                <div className={styles.actionRow}><button className={styles.secondaryButton} disabled={this.state.isWorking} onClick={saveEpisode}>{saveLabel}</button></div>
                {episode.scenes.map((scene, sceneIndex) => this.renderScene(scene, sceneIndex))}
                <div className={styles.bottomSave}><button className={styles.secondaryButton} disabled={this.state.isWorking} onClick={saveEpisode}>{saveLabel}</button></div>
            </div> : null}
        </React.Fragment>);
    }
    renderVoice () {
        const voices = this.state.voices || {};
        return (<React.Fragment>
            <p className={styles.intro}>Tạo lời thoại bằng Gemini rồi thêm trực tiếp vào Sounds của nhân vật hoặc Sân khấu đang chọn.</p>
            <label className={styles.label} htmlFor="film-maker-ai-voice">Lời thoại</label>
            <textarea id="film-maker-ai-voice" className={styles.textarea} maxLength="500" value={this.state.voiceText} onChange={event => this.setState({voiceText: event.target.value})} />
            <label className={styles.label} htmlFor="film-maker-ai-voice-choice">Giọng nói</label>
            <select id="film-maker-ai-voice-choice" className={styles.select} value={this.state.selectedVoice} onChange={event => this.setState({selectedVoice: event.target.value})}>
                {voices.characterA ? <option value={voices.characterA}>Nhân vật A · {voices.characterA}</option> : null}
                {voices.characterB ? <option value={voices.characterB}>Nhân vật B · {voices.characterB}</option> : null}
                {voices.narrator ? <option value={voices.narrator}>Dẫn chuyện / thú cưng · {voices.narrator}</option> : null}
            </select>
            <div className={styles.actionRow}><button className={styles.primaryButton} disabled={this.state.isWorking || !this.state.voices} onClick={this.handleGenerateSpeech}>Tạo và thêm vào Sounds</button></div>
        </React.Fragment>);
    }
    renderImage () {
        const image = this.state.image;
        const characters = this.state.selectedSeries ? this.state.selectedSeries.characters : [];
        return (<React.Fragment>
            <p className={styles.intro}>Tạo ảnh từ prompt của scene hoặc character bible, rồi thêm vào Scratch và/hoặc thư viện dùng chung của series.</p>
            <label className={styles.label} htmlFor="film-maker-ai-asset-type">Loại tài nguyên</label>
            <select id="film-maker-ai-asset-type" className={styles.select} value={this.state.assetType} onChange={event => this.setState({assetType: event.target.value, image: null})}>
                <option value="background">Phông nền (Backdrop)</option><option value="costume">Trang phục nhân vật (Costume)</option><option value="prop">Đạo cụ (Costume)</option>
            </select>
            {this.state.selectedSeries ? <React.Fragment><label className={styles.label} htmlFor="film-maker-ai-asset-character">Gắn với nhân vật series</label><select id="film-maker-ai-asset-character" className={styles.select} value={this.state.assetCharacterId} onChange={event => this.setState({assetCharacterId: event.target.value})}><option value="">Không gắn riêng</option>{characters.map(character => <option key={character.id} value={character.id}>{character.name}</option>)}</select></React.Fragment> : null}
            <label className={styles.label} htmlFor="film-maker-ai-image-prompt">Mô tả hình ảnh</label>
            <textarea id="film-maker-ai-image-prompt" className={styles.textarea} maxLength="2000" value={this.state.imagePrompt} onChange={event => this.setState({imagePrompt: event.target.value})} />
            <label className={styles.label} htmlFor="film-maker-ai-image-name">Tên tài nguyên</label>
            <input id="film-maker-ai-image-name" className={styles.input} maxLength="80" value={this.state.imageName} onChange={event => this.setState({imageName: event.target.value})} />
            <div className={styles.actionRow}><button className={styles.primaryButton} disabled={this.state.isWorking} onClick={this.handleGenerateImage}>Tạo ảnh</button>{image ? <button className={styles.secondaryButton} disabled={this.state.isWorking} onClick={this.handleAddImage}>Thêm vào Scratch</button> : null}{image && this.state.selectedSeries ? <button className={styles.secondaryButton} disabled={this.state.isWorking} onClick={this.handleSaveImageToSeries}>Lưu vào series</button> : null}</div>
            {image ? <img className={styles.preview} alt="Xem trước tài nguyên AI tạo" src={`data:${image.mimeType};base64,${image.imageBase64}`} /> : null}
        </React.Fragment>);
    }
    renderViewSwitch (value, onChange, label) {
        return <div className={styles.viewSwitch} role="group" aria-label={label}>
            <button className={value === 'grid' ? styles.activeViewButton : styles.viewButton} onClick={() => onChange('grid')}>▦ Lưới</button>
            <button className={value === 'list' ? styles.activeViewButton : styles.viewButton} onClick={() => onChange('list')}>☰ Danh sách</button>
        </div>;
    }
    renderAssetEntry (asset) {
        return <article className={styles.assetCard} key={asset.id}>
            <div className={styles.thumbnailWrap}><img className={styles.assetThumbnail} src={asset.url} alt={asset.name} /></div>
            <div className={styles.cardContent}>
                <strong>{asset.name}</strong>
                <span className={styles.meta}>{asset.kind} · {Math.max(1, Math.round((asset.byteSize || 0) / 1024))} KB</span>
                <p className={styles.cardPrompt}>{asset.prompt || 'Chưa có mô tả prompt.'}</p>
                <span className={styles.meta}>Tạo: {formatDateTime(asset.createdAt)}</span>
                <div className={styles.cardActions}><button className={styles.linkButton} onClick={() => this.loadSeriesAsset(asset)}>Nạp vào Scratch</button><button className={styles.linkButton} onClick={() => this.beginAssetEdit(asset)}>Sửa</button></div>
            </div>
        </article>;
    }
    renderEpisodeEntry (episode) {
        return <article className={styles.episodeCard} key={episode.id}>
            <div className={styles.thumbnailWrap}><img className={styles.episodeThumbnail} src={episode.thumbnailUrl} alt={`Thumbnail ${episode.title}`} onError={event => { event.currentTarget.style.display = 'none'; }} /><span className={styles.thumbnailFallback}>Tập {episode.number}</span></div>
            <div className={styles.cardContent}>
                <strong>Tập {episode.number}: {episode.title}</strong>
                <p className={styles.cardPrompt}>{episode.logline || 'Chưa có logline.'}</p>
                <span className={styles.meta}>Tạo: {formatDateTime(episode.createdAt)}</span>
                <span className={styles.meta}>Mở/sửa gần nhất: {formatDateTime(episode.lastOpenedAt || episode.updatedAt)}</span>
                <div className={styles.cardActions}><button className={styles.linkButton} onClick={() => this.loadEpisode(episode.id)}>Mở screenplay</button>{episode.videoUrl ? <a className={styles.linkButton} href={episode.videoUrl} target="_blank" rel="noreferrer">Video output</a> : <span className={styles.unavailable}>Chưa có video output</span>}</div>
            </div>
        </article>;
    }
    renderSeries () {
        const series = this.state.selectedSeries;
        const draft = this.state.seriesDraft;
        const assetDraft = this.state.assetDraft;
        return (<React.Fragment>
            <p className={styles.intro}>Series giữ character bible, asset dùng chung và các episode có thể mở lại để tiếp tục viết kịch bản hoặc tạo resource bằng AI.</p>
            <div className={styles.actionRow}><select className={styles.select} value={this.state.selectedSeriesId} onChange={event => this.selectSeries(event.target.value)}><option value="">Chọn series…</option>{this.state.series.map(item => <option key={item.id} value={item.id}>{item.title} · {item.episodeCount || 0} tập</option>)}</select><button className={styles.secondaryButton} onClick={() => this.setState({creatingSeries: true, selectedSeriesId: '', selectedSeries: null, seriesDraft: {title: '', premise: '', visualStyle: ''}})}>Tạo series</button></div>
            {(series || this.state.creatingSeries) ? <div className={styles.seriesEditor}>
                <label className={styles.label}>Tên series</label><input className={styles.input} value={draft.title} onChange={event => this.setState({seriesDraft: {...draft, title: event.target.value}})} />
                <label className={styles.label}>Premise / thế giới phim</label><textarea className={styles.compactTextarea} value={draft.premise} onChange={event => this.setState({seriesDraft: {...draft, premise: event.target.value}})} />
                <label className={styles.label}>Visual style chung</label><textarea className={styles.compactTextarea} value={draft.visualStyle} onChange={event => this.setState({seriesDraft: {...draft, visualStyle: event.target.value}})} />
                <div className={styles.actionRow}>{series ? <button className={styles.primaryButton} disabled={this.state.isWorking} onClick={this.handleSaveSeries}>Lưu series bible</button> : <button className={styles.primaryButton} disabled={this.state.isWorking} onClick={this.handleCreateSeries}>Tạo series</button>}</div>
            </div> : null}
            {series ? <React.Fragment>
                <section className={styles.collection}><h3>Character bible</h3>{series.characters.map(character => <div className={styles.collectionItem} key={character.id}><div><strong>{character.name}</strong><span>{character.role}</span><p>{character.description}</p></div><button className={styles.linkButton} onClick={() => this.setState({editingCharacterId: character.id, characterDraft: {...character}})}>Sửa</button></div>)}
                    <div className={styles.characterForm}><label className={styles.label}>Tên nhân vật</label><input className={styles.input} value={this.state.characterDraft.name} onChange={event => this.setState({characterDraft: {...this.state.characterDraft, name: event.target.value}})} /><label className={styles.label}>Vai trò</label><input className={styles.input} value={this.state.characterDraft.role} onChange={event => this.setState({characterDraft: {...this.state.characterDraft, role: event.target.value}})} /><label className={styles.label}>Tính cách / mô tả</label><textarea className={styles.compactTextarea} value={this.state.characterDraft.description} onChange={event => this.setState({characterDraft: {...this.state.characterDraft, description: event.target.value}})} /><label className={styles.label}>Design prompt cố định</label><textarea className={styles.compactTextarea} value={this.state.characterDraft.designPrompt} onChange={event => this.setState({characterDraft: {...this.state.characterDraft, designPrompt: event.target.value}})} /><div className={styles.actionRow}><button className={styles.primaryButton} disabled={this.state.isWorking} onClick={this.handleSaveCharacter}>{this.state.editingCharacterId ? 'Cập nhật nhân vật' : 'Thêm nhân vật'}</button></div></div>
                </section>
                <section className={styles.collection}><div className={styles.collectionHeader}><h3>Thư viện asset dùng chung</h3>{this.renderViewSwitch(this.state.assetView, value => this.setState({assetView: value}), 'Kiểu xem asset')}</div>
                    <div className={styles.assetEditor}><strong>{this.state.editingAssetId ? 'Sửa asset' : 'Thêm ảnh từ máy'}</strong><label className={styles.label}>Tên asset</label><input className={styles.input} value={assetDraft.name} onChange={event => this.setState({assetDraft: {...assetDraft, name: event.target.value}})} /><label className={styles.label}>Loại asset</label><select className={styles.select} value={assetDraft.kind} onChange={event => this.setState({assetDraft: {...assetDraft, kind: event.target.value}})}><option value="background">Phông nền</option><option value="costume">Costume</option><option value="prop">Đạo cụ</option></select><label className={styles.label}>Nhân vật (nếu có)</label><select className={styles.select} value={assetDraft.characterId} onChange={event => this.setState({assetDraft: {...assetDraft, characterId: event.target.value}})}><option value="">Không gắn riêng</option>{series.characters.map(character => <option key={character.id} value={character.id}>{character.name}</option>)}</select><label className={styles.label}>Prompt / ghi chú</label><textarea className={styles.compactTextarea} value={assetDraft.prompt} onChange={event => this.setState({assetDraft: {...assetDraft, prompt: event.target.value}})} /><div className={styles.actionRow}>{this.state.editingAssetId ? <React.Fragment><button className={styles.primaryButton} disabled={this.state.isWorking} onClick={this.handleSaveSeriesAsset}>Lưu chỉnh sửa</button><button className={styles.secondaryButton} onClick={() => this.beginAssetEdit()}>Thêm asset mới</button></React.Fragment> : <label className={styles.uploadButton}>Tải PNG/JPEG từ máy<input className={styles.fileInput} type="file" accept="image/png,image/jpeg" onChange={this.handleUploadSeriesAsset} /></label>}</div></div>
                    {series.assets.length ? <div className={this.state.assetView === 'grid' ? styles.assetGrid : styles.assetList}>{series.assets.map(asset => this.renderAssetEntry(asset))}</div> : <p className={styles.empty}>Chưa có asset. Tạo ảnh bằng AI hoặc tải file PNG/JPEG lên từ máy.</p>}</section>
                <section className={styles.collection}><div className={styles.collectionHeader}><h3>Episodes</h3>{this.renderViewSwitch(this.state.episodeView, value => this.setState({episodeView: value}), 'Kiểu xem episode')}</div>{series.episodes.length ? <div className={this.state.episodeView === 'grid' ? styles.episodeGrid : styles.episodeList}>{series.episodes.map(episode => this.renderEpisodeEntry(episode))}</div> : <p className={styles.empty}>Chưa có episode được lưu.</p>}</section>
            </React.Fragment> : null}
        </React.Fragment>);
    }
    render () {
        return (<Modal className={styles.modal} innerClassName={styles.modalShell} contentLabel="AI Assistant" onRequestClose={this.props.onRequestClose}>
            <Box className={styles.content} direction="column" grow={1}>
                <div className={styles.tabs} role="tablist" aria-label="Công cụ AI"><button className={this.state.activeTab === 'story' ? styles.activeTab : styles.tab} onClick={() => this.setTab('story')}>Kịch bản</button><button className={this.state.activeTab === 'series' ? styles.activeTab : styles.tab} onClick={() => this.setTab('series')}>Series</button><button className={this.state.activeTab === 'voice' ? styles.activeTab : styles.tab} onClick={() => this.setTab('voice')}>Giọng nói</button><button className={this.state.activeTab === 'image' ? styles.activeTab : styles.tab} onClick={() => this.setTab('image')}>Hình ảnh</button></div>
                <div className={styles.body}>{this.state.activeTab === 'story' ? this.renderStory() : null}{this.state.activeTab === 'series' ? this.renderSeries() : null}{this.state.activeTab === 'voice' ? this.renderVoice() : null}{this.state.activeTab === 'image' ? this.renderImage() : null}</div>
                {this.state.status ? <div className={styles.status} role="status">{this.state.status}</div> : null}
                {this.state.error ? <div className={styles.error} role="alert">{this.state.error}</div> : null}
            </Box>
        </Modal>);
    }
}

FilmMakerAIAssist.propTypes = {
    onRequestClose: PropTypes.func.isRequired,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vm: PropTypes.instanceOf(VM).isRequired
};

export default FilmMakerAIAssist;
